import { AddOutlined, DeleteOutlined, MoreVert } from '@mui/icons-material';
import {
    Box,
    Divider,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Menu,
    MenuItem,
} from '@mui/material';
import { useState, type MouseEvent, type RefObject } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CardHeaderAction } from '~/components/DetailsCard';
import LinkTile from '~/components/LinkTile';
import MenuAction from '~/components/Menu/MenuAction';
import { besideSidebarPoint } from '~/components/Properties/PropertiesSidePanel';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { ApprovalStatusLabel } from '~/components/Structures/ApprovalMark';
import { RegistrationView } from '~/domain/projections/views';

interface RegistrationMenuProps {
    /** The card the menu opens beside, its top level with the card's. */
    cardRef: RefObject<HTMLElement>;
    isApproved?: boolean;
    isDisabled?: boolean;
    hasSharedFrameOfReference?: boolean;
    availableRegistrations?: ReadonlyArray<RegistrationView>;
    activeRegistrationSopUid?: string;
    onCreateNew: () => void;
    onDelete: () => void;
    onChangeRegistration?: (sopInstanceUid: string) => void;
}

/**
 * The More menu of a registration, opened beside the sidebar with its top
 * level with the registration's card: the image's registrations to change
 * to, each with its link mark, its label and its approval status, listed
 * under a heading with the current one selected, then create a new one and
 * delete it. Every row is a direct item of the menu, so the arrow keys reach
 * each one. Delete is disabled while the registration is approved, and
 * every item while one is being computed.
 */
export default function RegistrationMenu({
    cardRef,
    isApproved = false,
    isDisabled = false,
    hasSharedFrameOfReference = false,
    availableRegistrations = [],
    activeRegistrationSopUid,
    onCreateNew,
    onDelete,
    onChangeRegistration,
}: RegistrationMenuProps) {
    const intl = useIntl();
    const [position, setPosition] = useState<{ left: number; top: number }>();

    const canChangeRegistration =
        availableRegistrations.length > 1 && onChangeRegistration !== undefined;

    const handleOpen = (event: MouseEvent<HTMLElement>) => {
        const opener = event.currentTarget;
        setPosition((current) =>
            current
                ? undefined
                : {
                      left: besideSidebarPoint(opener).left,
                      top: (cardRef.current ?? opener).getBoundingClientRect()
                          .top,
                  }
        );
    };
    const handleClose = () => setPosition(undefined);
    const act = (action: () => void) => () => {
        handleClose();
        action();
    };

    const handleSelectRegistration = (sopInstanceUid: string) => {
        if (
            onChangeRegistration &&
            sopInstanceUid !== activeRegistrationSopUid
        ) {
            onChangeRegistration(sopInstanceUid);
        }
        handleClose();
    };

    return (
        <>
            <CardHeaderAction
                Icon={MoreVert}
                label={intl.formatMessage({ id: 'moreActions' })}
                onClick={handleOpen}
                selected={position !== undefined}
                opensMenu
                data-testid="registration-menu-button"
            />
            <Menu
                open={position !== undefined}
                onClose={handleClose}
                anchorReference="anchorPosition"
                anchorPosition={position}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                marginThreshold={8}
                data-testid="registration-more-menu"
                slotProps={{ paper: { sx: { width: 260, mt: 0 } } }}
            >
                {canChangeRegistration && (
                    <ListSubheader>
                        <FormattedMessage id="changeRegistration" />
                    </ListSubheader>
                )}
                {canChangeRegistration &&
                    availableRegistrations.map((reg) => (
                        <MenuItem
                            key={reg.sopInstanceUid}
                            selected={
                                reg.sopInstanceUid === activeRegistrationSopUid
                            }
                            onClick={() =>
                                handleSelectRegistration(reg.sopInstanceUid)
                            }
                        >
                            <ListItemIcon>
                                <LinkTile
                                    selected={
                                        reg.sopInstanceUid ===
                                        activeRegistrationSopUid
                                    }
                                />
                            </ListItemIcon>
                            <ListItemText
                                primaryTypographyProps={{ noWrap: true }}
                                sx={{ minWidth: 0 }}
                            >
                                <SensitiveInfoTypography>
                                    {reg.contentLabel ||
                                        reg.sopInstanceUid.slice(0, 8)}
                                </SensitiveInfoTypography>
                            </ListItemText>
                            <Box
                                display="flex"
                                flexShrink={0}
                                color="text.secondary"
                            >
                                <ApprovalStatusLabel
                                    status={reg.approvalStatus}
                                    size={14}
                                />
                            </Box>
                        </MenuItem>
                    ))}
                {canChangeRegistration && <Divider />}
                <MenuAction
                    icon={<AddOutlined />}
                    label={<FormattedMessage id="registrationNew" />}
                    onClick={act(onCreateNew)}
                    disabled={hasSharedFrameOfReference || isDisabled}
                />
                <MenuAction
                    icon={<DeleteOutlined />}
                    label={<FormattedMessage id="deleteRegistration" />}
                    onClick={act(onDelete)}
                    disabled={isApproved || isDisabled}
                    destructive
                />
            </Menu>
        </>
    );
}
